import { OptimizelyDecision, setLogLevel } from '@optimizely/optimizely-sdk'
import Script from 'next/script'
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { env } from '@/env.mjs'

import { eventKeys } from './optimizely-event-keys'
type EventKey = keyof typeof eventKeys
type Events = (typeof eventKeys)[EventKey]

export interface ABTestingContextProps {
  ikeaScriptUrls: string[]
  sdkKey: string
  children: ReactNode
}

export interface ABTestingContextValue {
  isFeatureEnabled: (flagName: string, flagAttributes?: object) => boolean
  decide: (
    flagKey: string,
    flagAttributes?: object
  ) => OptimizelyDecision | null
  track: (eventKey: Events, attributes?: object, eventTags?: object) => void
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

export const ABTestingContext = createContext<ABTestingContextValue>({
  isFeatureEnabled: () => false,
  decide: () => null,
  track: noop,
})

export const ABTestingContextProvider: FC<ABTestingContextProps> = ({
  ikeaScriptUrls = [],
  sdkKey,
  children,
}) => {
  const eventsThatHappenedBeforeOptimizelyIsReady = useRef<
    Parameters<ABTestingContextValue['track']>[]
  >([])
  const [context, setContext] = useState<ABTestingContextValue>({
    isFeatureEnabled: () => false,
    decide: () => null,
    track: (...args) => {
      eventsThatHappenedBeforeOptimizelyIsReady.current.push(args)
    },
  })

  const initFragment = useCallback(() => {
    if (!window?.ikea?.experiment) return

    if (env.NEXT_PUBLIC_ENV === 'LOCAL') {
      window.ikea = window.ikea || {}
      window.ikea.cookieConsent = {
        hasConsent: () => true,
        interactedWithBanner: true,
      }
      window.ikea.pubsub = {
        subscribe: noop,
        unsubscribe: noop,
        publish: noop,
        events: () => [
          {
            event: 'initialized',
            namespace: 'authentication',
          },
        ],
      }
    }

    const flushOptimizelyEvents = (track: ABTestingContextValue['track']) => {
      if (eventsThatHappenedBeforeOptimizelyIsReady.current.length) {
        eventsThatHappenedBeforeOptimizelyIsReady.current.forEach(
          ([eventKey, attributes, eventTags]) => {
            track(eventKey, attributes, eventTags)
          }
        )
        eventsThatHappenedBeforeOptimizelyIsReady.current = []
      }
    }

    try {
      window.ikea.experiment.init(
        sdkKey,
        { waitLoginStatus: false, attributePrefix: 'asisonline_shop__' },
        {},
        (wrapper: ABTestingContextValue) => {
          setContext({
            track: wrapper.track.bind(wrapper),
            decide: wrapper.decide.bind(wrapper),
            isFeatureEnabled: wrapper.isFeatureEnabled.bind(wrapper),
          })
          setLogLevel('debug')
          flushOptimizelyEvents(wrapper.track.bind(wrapper))
        }
      )
    } catch (e) {
      console.error('Error while loading AB Testing fragment', e)
    }
  }, [sdkKey])

  useEffect(() => {
    initFragment()
  }, [initFragment])

  return (
    <ABTestingContext.Provider value={context}>
      {ikeaScriptUrls.map((sourceUrl: string) => (
        <Script
          id={sourceUrl}
          key={sourceUrl}
          src={sourceUrl}
          onLoad={initFragment}
        />
      ))}
      {children}
    </ABTestingContext.Provider>
  )
}

export const useAbTesting = () => {
  const context = useContext(ABTestingContext)
  if (!context) {
    throw new Error('useAbTesting was used outside of its provider')
  }
  return context
}
